<script setup lang="ts">
import OperatorAccountMenu from './OperatorAccountMenu.vue';
import { onMounted, ref } from 'vue';

type ViewStatus =
  'IDLE' | 'LOADING' | 'SUCCESS' | 'EMPTY' | 'FORBIDDEN' | 'CONFIGURATION_ERROR' | 'ERROR';

const movementTypes = ['REPLENISHMENT', 'LOSS_EXPIRED', 'LOSS_DAMAGED'] as const;
type MovementType = (typeof movementTypes)[number];
const movementLabels: Record<MovementType, string> = {
  REPLENISHMENT: 'Réapprovisionnement',
  LOSS_EXPIRED: 'Produits périmés',
  LOSS_DAMAGED: 'Produits abîmés',
};
function unitLabel(value: string): string {
  return (
    ({ UNIT: 'Unité', KG: 'kg', G: 'g', L: 'L', ML: 'mL' } as Record<string, string>)[value] ??
    value
  );
}
function actorLabel(value: string): string {
  return (
    (
      { ADMIN: 'Administrateur', PROVIDER: 'Préparateur', SYSTEM: 'Automatique' } as Record<
        string,
        string
      >
    )[value] ?? 'Équipe'
  );
}

type InventoryBalance = {
  inventory_item_id: string;
  unit_of_measure: string;
  balance: number;
  projectionVersion: 1;
  stateRevision: number;
};

type InventoryMovement = {
  movement_id: string;
  movement_type: MovementType;
  inventory_item_id: string;
  unit_of_measure: string;
  quantity: number;
  recorded_at: string;
  tenant_id: string;
  property_id: string;
  actor_id: string;
  actor_class: string;
  box_id?: string;
};

type MovementPage = {
  movements: InventoryMovement[];
  nextCursor: string | null;
  projectionVersion: 1;
  stateRevision: number;
};

type LossPage = {
  losses: InventoryMovement[];
  nextCursor: string | null;
  projectionVersion: 1;
  stateRevision: number;
};

const balanceStatus = ref<ViewStatus>('IDLE');
const balance = ref<InventoryBalance | null>(null);
const balanceInventoryItemId = ref('');

const movementStatus = ref<ViewStatus>('IDLE');
const movements = ref<InventoryMovement[]>([]);
const movementNextCursor = ref<string | null>(null);
const movementInventoryItemId = ref('');
const movementType = ref<'' | MovementType>('');
const movementLoadingMore = ref(false);

const lossStatus = ref<ViewStatus>('IDLE');
const losses = ref<InventoryMovement[]>([]);
const lossNextCursor = ref<string | null>(null);
const lossLoadingMore = ref(false);

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isSafeRevision(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

function isInventoryBalance(value: unknown): value is InventoryBalance {
  return (
    isRecord(value) &&
    typeof value.inventory_item_id === 'string' &&
    typeof value.unit_of_measure === 'string' &&
    typeof value.balance === 'number' &&
    Number.isSafeInteger(value.balance) &&
    value.projectionVersion === 1 &&
    isSafeRevision(value.stateRevision)
  );
}

function isInventoryMovement(value: unknown, lossOnly = false): value is InventoryMovement {
  if (!isRecord(value)) return false;
  const type = value.movement_type;
  return (
    typeof value.movement_id === 'string' &&
    typeof type === 'string' &&
    movementTypes.includes(type as MovementType) &&
    (!lossOnly || ['LOSS_EXPIRED', 'LOSS_DAMAGED'].includes(type)) &&
    typeof value.inventory_item_id === 'string' &&
    typeof value.unit_of_measure === 'string' &&
    typeof value.quantity === 'number' &&
    Number.isSafeInteger(value.quantity) &&
    value.quantity > 0 &&
    typeof value.recorded_at === 'string' &&
    typeof value.tenant_id === 'string' &&
    typeof value.property_id === 'string' &&
    typeof value.actor_id === 'string' &&
    typeof value.actor_class === 'string' &&
    (value.box_id === undefined || typeof value.box_id === 'string')
  );
}

function isCursor(value: unknown): value is string | null {
  return value === null || (typeof value === 'string' && value.length > 0);
}

function isMovementPage(value: unknown): value is MovementPage {
  return (
    isRecord(value) &&
    Array.isArray(value.movements) &&
    value.movements.every((movement) => isInventoryMovement(movement)) &&
    isCursor(value.nextCursor) &&
    value.projectionVersion === 1 &&
    isSafeRevision(value.stateRevision)
  );
}

function isLossPage(value: unknown): value is LossPage {
  return (
    isRecord(value) &&
    Array.isArray(value.losses) &&
    value.losses.every((movement) => isInventoryMovement(movement, true)) &&
    isCursor(value.nextCursor) &&
    value.projectionVersion === 1 &&
    isSafeRevision(value.stateRevision)
  );
}

function failureStatus(status: number): ViewStatus {
  if (status === 403) return 'FORBIDDEN';
  if (status === 503) return 'CONFIGURATION_ERROR';
  return 'ERROR';
}

function statusMessage(status: ViewStatus): string {
  if (status === 'LOADING') return 'Chargement en cours…';
  if (status === 'FORBIDDEN') return 'Accès non autorisé.';
  if (status === 'CONFIGURATION_ERROR') {
    return 'La lecture Stock est indisponible. Une configuration serveur est requise.';
  }
  if (status === 'ERROR') return 'La lecture Stock est momentanément indisponible.';
  return '';
}

async function loadBalance(): Promise<void> {
  const inventoryItemId = balanceInventoryItemId.value.trim();
  balance.value = null;
  if (!inventoryItemId) {
    balanceStatus.value = 'IDLE';
    return;
  }
  balanceStatus.value = 'LOADING';
  const query = new globalThis.URLSearchParams({ inventory_item_id: inventoryItemId });
  try {
    const response = await globalThis.fetch(`/admin/api/inventory-balances?${query}`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) {
      balanceStatus.value = failureStatus(response.status);
      return;
    }
    const body: unknown = await response.json();
    if (!isInventoryBalance(body)) {
      balanceStatus.value = 'ERROR';
      return;
    }
    balance.value = body;
    balanceStatus.value = body.balance === 0 ? 'EMPTY' : 'SUCCESS';
  } catch {
    balanceStatus.value = 'ERROR';
  }
}

function movementQuery(cursor: string | null): string {
  const query = new globalThis.URLSearchParams();
  const inventoryItemId = movementInventoryItemId.value.trim();
  if (inventoryItemId) query.set('inventory_item_id', inventoryItemId);
  if (movementType.value) query.set('movement_type', movementType.value);
  if (cursor !== null) query.set('cursor', cursor);
  const encoded = query.toString();
  return encoded
    ? `/admin/api/inventory-movements?${encoded}`
    : '/admin/api/inventory-movements';
}

async function loadMovements(append = false): Promise<void> {
  const cursor = append ? movementNextCursor.value : null;
  if (append && cursor === null) return;
  if (append) movementLoadingMore.value = true;
  else {
    movementStatus.value = 'LOADING';
    movements.value = [];
    movementNextCursor.value = null;
  }
  try {
    const response = await globalThis.fetch(movementQuery(cursor), {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) {
      movementStatus.value = failureStatus(response.status);
      return;
    }
    const body: unknown = await response.json();
    if (!isMovementPage(body)) {
      movementStatus.value = 'ERROR';
      return;
    }
    movements.value = append ? [...movements.value, ...body.movements] : body.movements;
    movementNextCursor.value = body.nextCursor;
    movementStatus.value = movements.value.length === 0 ? 'EMPTY' : 'SUCCESS';
  } catch {
    movementStatus.value = 'ERROR';
  } finally {
    movementLoadingMore.value = false;
  }
}

function lossQuery(cursor: string | null): string {
  if (cursor === null) return '/admin/api/inventory-losses';
  const query = new globalThis.URLSearchParams({ cursor });
  return `/admin/api/inventory-losses?${query}`;
}

async function loadLosses(append = false): Promise<void> {
  const cursor = append ? lossNextCursor.value : null;
  if (append && cursor === null) return;
  if (append) lossLoadingMore.value = true;
  else {
    lossStatus.value = 'LOADING';
    losses.value = [];
    lossNextCursor.value = null;
  }
  try {
    const response = await globalThis.fetch(lossQuery(cursor), {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) {
      lossStatus.value = failureStatus(response.status);
      return;
    }
    const body: unknown = await response.json();
    if (!isLossPage(body)) {
      lossStatus.value = 'ERROR';
      return;
    }
    losses.value = append ? [...losses.value, ...body.losses] : body.losses;
    lossNextCursor.value = body.nextCursor;
    lossStatus.value = losses.value.length === 0 ? 'EMPTY' : 'SUCCESS';
  } catch {
    lossStatus.value = 'ERROR';
  } finally {
    lossLoadingMore.value = false;
  }
}

function formatTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

onMounted(() => {
  void loadMovements();
  void loadLosses();
});
</script>

<template>
  <main id="main" class="inventory-shell">
    <header class="inventory-header">
      <div class="mvpa-operational-brand">
        <OperatorAccountMenu /><strong>OTEM Upsells Manager</strong>
      </div>
      <p class="inventory-eyebrow">OTEM · Administration</p>
      <h1>Stock</h1>
      <p>Consultez les soldes, les mouvements et les pertes en lecture seule.</p>
      <nav class="inventory-navigation" aria-label="Navigation administration">
        <a href="/admin">Tableau de bord</a>
        <a href="/admin/catalog">Catalogue</a>
        <a href="/admin/boxes">Boxs</a>
        <a href="/admin/providers-apartments">Comptes</a>
        <span aria-current="page">Stock</span>
      </nav>
    </header>

    <div class="inventory-grid">
      <section
        class="inventory-panel"
        data-testid="inventory-balances"
        aria-labelledby="inventory-balances-title"
      >
        <div class="inventory-section-heading">
          <div>
            <p class="inventory-kicker">Lecture article</p>
            <h2 id="inventory-balances-title">Stock disponible</h2>
          </div>
        </div>
        <form
          class="inventory-filter"
          aria-label="Consulter le stock"
          @submit.prevent="loadBalance"
        >
          <label for="inventory-balance-item">Article d’inventaire</label>
          <div class="inventory-filter-line">
            <input
              id="inventory-balance-item"
              v-model="balanceInventoryItemId"
              name="inventory_item_id"
              autocomplete="off"
              required
            />
            <button type="submit">Consulter</button>
          </div>
        </form>
        <p v-if="balanceStatus === 'IDLE'" class="inventory-state" role="status">
          Saisissez un article pour consulter sa quantité disponible.
        </p>
        <p v-else-if="balanceStatus === 'EMPTY'" class="inventory-state" role="status">
          Aucun stock disponible pour cet article.
        </p>
        <p
          v-else-if="!['SUCCESS'].includes(balanceStatus)"
          class="inventory-state"
          :class="{
            'inventory-state-error': ['FORBIDDEN', 'CONFIGURATION_ERROR', 'ERROR'].includes(
              balanceStatus,
            ),
          }"
          :role="
            ['FORBIDDEN', 'CONFIGURATION_ERROR', 'ERROR'].includes(balanceStatus)
              ? 'alert'
              : 'status'
          "
        >
          {{ statusMessage(balanceStatus) }}
        </p>
        <dl v-if="balance" class="inventory-balance-details">
          <div>
            <dt>Article</dt>
            <dd>{{ balance.inventory_item_id }}</dd>
          </div>
          <div>
            <dt>Unité</dt>
            <dd>{{ unitLabel(balance.unit_of_measure) }}</dd>
          </div>
          <div>
            <dt>Quantité disponible</dt>
            <dd>{{ balance.balance }}</dd>
          </div>
        </dl>
      </section>

      <section
        class="inventory-panel inventory-panel-wide"
        data-testid="inventory-movements"
        aria-labelledby="inventory-movements-title"
      >
        <div class="inventory-section-heading">
          <div>
            <p class="inventory-kicker">Entrées et sorties</p>
            <h2 id="inventory-movements-title">Mouvements</h2>
          </div>
        </div>
        <form
          class="inventory-filter inventory-movement-filters"
          aria-label="Filtrer les mouvements"
          @submit.prevent="loadMovements(false)"
        >
          <label for="inventory-movement-item">Article d’inventaire</label>
          <input
            id="inventory-movement-item"
            v-model="movementInventoryItemId"
            name="inventory_item_id"
            autocomplete="off"
          />
          <label for="inventory-movement-type">Type de mouvement</label>
          <select id="inventory-movement-type" v-model="movementType" name="movement_type">
            <option value="">Tous les types</option>
            <option v-for="type in movementTypes" :key="type" :value="type">
              {{ movementLabels[type] }}
            </option>
          </select>
          <button type="submit">Appliquer</button>
        </form>
        <p v-if="movementStatus === 'EMPTY'" class="inventory-state" role="status">
          Aucun mouvement à afficher.
        </p>
        <p
          v-else-if="!['SUCCESS'].includes(movementStatus)"
          class="inventory-state"
          :class="{
            'inventory-state-error': ['FORBIDDEN', 'CONFIGURATION_ERROR', 'ERROR'].includes(
              movementStatus,
            ),
          }"
          :role="
            ['FORBIDDEN', 'CONFIGURATION_ERROR', 'ERROR'].includes(movementStatus)
              ? 'alert'
              : 'status'
          "
        >
          {{ statusMessage(movementStatus) }}
        </p>
        <div v-if="movementStatus === 'SUCCESS'" class="inventory-table-wrap" tabindex="0">
          <table>
            <caption class="visually-hidden">
              Historique des mouvements d’inventaire
            </caption>
            <thead>
              <tr>
                <th scope="col">Mouvement</th>
                <th scope="col">Type</th>
                <th scope="col">Article</th>
                <th scope="col">Unité</th>
                <th scope="col">Quantité</th>
                <th scope="col">Enregistré</th>
                <th scope="col">Organisation</th>
                <th scope="col">Logement</th>
                <th scope="col">Enregistré par</th>
                <th scope="col">Box</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="movement in movements"
                :key="movement.movement_id"
                :data-movement-id="movement.movement_id"
              >
                <td>{{ movement.movement_id }}</td>
                <td>{{ movementLabels[movement.movement_type] }}</td>
                <td>{{ movement.inventory_item_id }}</td>
                <td>{{ unitLabel(movement.unit_of_measure) }}</td>
                <td>{{ movement.quantity }}</td>
                <td>
                  <time :datetime="movement.recorded_at">{{
                    formatTimestamp(movement.recorded_at)
                  }}</time>
                </td>
                <td>{{ movement.tenant_id }}</td>
                <td>{{ movement.property_id }}</td>
                <td>{{ movement.actor_id }} · {{ actorLabel(movement.actor_class) }}</td>
                <td>{{ movement.box_id ?? '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <button
          v-if="movementStatus === 'SUCCESS' && movementNextCursor"
          class="inventory-load-more"
          type="button"
          :disabled="movementLoadingMore"
          @click="loadMovements(true)"
        >
          {{ movementLoadingMore ? 'Chargement…' : 'Charger plus de mouvements' }}
        </button>
      </section>

      <section
        class="inventory-panel inventory-panel-wide"
        data-testid="inventory-losses"
        aria-labelledby="inventory-losses-title"
      >
        <div class="inventory-section-heading">
          <div>
            <p class="inventory-kicker">Produits retirés</p>
            <h2 id="inventory-losses-title">Pertes</h2>
          </div>
        </div>
        <p v-if="lossStatus === 'EMPTY'" class="inventory-state" role="status">
          Aucune perte à afficher.
        </p>
        <p
          v-else-if="!['SUCCESS'].includes(lossStatus)"
          class="inventory-state"
          :class="{
            'inventory-state-error': ['FORBIDDEN', 'CONFIGURATION_ERROR', 'ERROR'].includes(
              lossStatus,
            ),
          }"
          :role="
            ['FORBIDDEN', 'CONFIGURATION_ERROR', 'ERROR'].includes(lossStatus)
              ? 'alert'
              : 'status'
          "
        >
          {{ statusMessage(lossStatus) }}
        </p>
        <div v-if="lossStatus === 'SUCCESS'" class="inventory-table-wrap" tabindex="0">
          <table>
            <caption class="visually-hidden">
              Pertes d’inventaire
            </caption>
            <thead>
              <tr>
                <th scope="col">Mouvement</th>
                <th scope="col">Type</th>
                <th scope="col">Article</th>
                <th scope="col">Unité</th>
                <th scope="col">Quantité</th>
                <th scope="col">Enregistré</th>
                <th scope="col">Organisation</th>
                <th scope="col">Logement</th>
                <th scope="col">Enregistré par</th>
                <th scope="col">Box</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="loss in losses"
                :key="loss.movement_id"
                :data-loss-id="loss.movement_id"
              >
                <td>{{ loss.movement_id }}</td>
                <td>{{ movementLabels[loss.movement_type] }}</td>
                <td>{{ loss.inventory_item_id }}</td>
                <td>{{ unitLabel(loss.unit_of_measure) }}</td>
                <td>{{ loss.quantity }}</td>
                <td>
                  <time :datetime="loss.recorded_at">{{
                    formatTimestamp(loss.recorded_at)
                  }}</time>
                </td>
                <td>{{ loss.tenant_id }}</td>
                <td>{{ loss.property_id }}</td>
                <td>{{ loss.actor_id }} · {{ actorLabel(loss.actor_class) }}</td>
                <td>{{ loss.box_id ?? '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <button
          v-if="lossStatus === 'SUCCESS' && lossNextCursor"
          class="inventory-load-more"
          type="button"
          :disabled="lossLoadingMore"
          @click="loadLosses(true)"
        >
          {{ lossLoadingMore ? 'Chargement…' : 'Charger plus de pertes' }}
        </button>
      </section>
    </div>
  </main>
</template>

<style scoped>
.inventory-shell {
  min-height: 100vh;
  padding: clamp(1rem, 3vw, 2.5rem);
  color: #172033;
  background: #f4f7fb;
}

.inventory-header,
.inventory-panel {
  min-width: 0;
  border: 1px solid #d8e0ec;
  border-radius: 1rem;
  background: #ffffff;
  box-shadow: 0 0.75rem 2rem rgb(11 35 65 / 8%);
}

.inventory-header {
  max-width: 90rem;
  margin: 0 auto 1.5rem;
  padding: clamp(1.25rem, 3vw, 2rem);
}

.inventory-header h1,
.inventory-panel h2 {
  margin: 0;
  color: #0b2341;
}

.inventory-header > p:not(.inventory-eyebrow) {
  max-width: 48rem;
  margin: 0.5rem 0 0;
  color: #526075;
}

.inventory-eyebrow,
.inventory-kicker {
  margin: 0 0 0.4rem;
  color: #315f91;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.inventory-navigation {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-top: 1.25rem;
}

.inventory-navigation a,
.inventory-navigation span,
.inventory-filter button,
.inventory-load-more {
  min-height: 2.75rem;
  padding: 0.68rem 0.95rem;
  border: 1px solid #0b2341;
  border-radius: 0.7rem;
  font: inherit;
  font-weight: 700;
}

.inventory-navigation a {
  color: #0b2341;
  text-decoration: none;
  background: #ffffff;
}

.inventory-navigation span,
.inventory-filter button,
.inventory-load-more {
  color: #ffffff;
  background: #0b2341;
}

.inventory-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  min-width: 0;
  gap: 1.25rem;
  max-width: 90rem;
  margin: 0 auto;
}

.inventory-panel {
  padding: clamp(1rem, 2.5vw, 1.5rem);
}

.inventory-section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.inventory-filter {
  display: grid;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.inventory-filter label {
  color: #34445c;
  font-weight: 700;
}

.inventory-filter-line {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.inventory-filter input,
.inventory-filter select {
  min-width: 0;
  min-height: 2.75rem;
  padding: 0.65rem 0.75rem;
  border: 1px solid #8c9bb0;
  border-radius: 0.65rem;
  color: #172033;
  background: #ffffff;
  font: inherit;
}

.inventory-filter-line input {
  flex: 1 1 14rem;
}

.inventory-filter button,
.inventory-load-more {
  cursor: pointer;
}

.inventory-filter button:focus-visible,
.inventory-load-more:focus-visible,
.inventory-navigation a:focus-visible,
.inventory-table-wrap:focus-visible,
.inventory-filter input:focus-visible,
.inventory-filter select:focus-visible {
  outline: 3px solid #f4a261;
  outline-offset: 2px;
}

.inventory-movement-filters {
  grid-template-columns: minmax(0, 1fr);
}

.inventory-state {
  margin: 1rem 0 0;
  padding: 0.9rem 1rem;
  border-radius: 0.7rem;
  color: #34445c;
  background: #edf3f9;
}

.inventory-state-error {
  color: #762529;
  background: #fff0f0;
}

.inventory-balance-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
  gap: 0.75rem;
  margin: 1rem 0 0;
}

.inventory-balance-details div {
  min-width: 0;
  padding: 0.85rem;
  border-radius: 0.7rem;
  background: #edf3f9;
}

.inventory-balance-details dt {
  color: #526075;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
}

.inventory-balance-details dd {
  margin: 0.3rem 0 0;
  overflow-wrap: anywhere;
  font-weight: 700;
}

.inventory-table-wrap {
  max-width: 100%;
  overflow-x: auto;
  border: 1px solid #d8e0ec;
  border-radius: 0.75rem;
}

table {
  width: 100%;
  min-width: 72rem;
  border-collapse: collapse;
}

th,
td {
  padding: 0.75rem;
  border-bottom: 1px solid #d8e0ec;
  text-align: left;
  overflow-wrap: anywhere;
}

th {
  color: #ffffff;
  background: #0b2341;
}

tbody tr:last-child td {
  border-bottom: 0;
}

.inventory-load-more {
  margin-top: 1rem;
}

.inventory-load-more:disabled {
  cursor: wait;
  opacity: 0.7;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (min-width: 760px) {
  .inventory-grid {
    grid-template-columns: minmax(18rem, 0.7fr) minmax(0, 1.3fr);
  }

  .inventory-panel-wide {
    grid-column: 1 / -1;
  }

  .inventory-movement-filters {
    grid-template-columns: minmax(0, 1fr) minmax(12rem, 0.6fr) auto;
    align-items: end;
  }

  .inventory-movement-filters label {
    grid-row: 1;
  }
}
</style>
